import i18n from '../../../i18n';

export type MonitoringIntervalUnit = 'DAY' | 'MONTH';

/** How often a project's prompts are sent to the models: once per window of `value` days or months. */
export interface MonitoringInterval {
  value: number;
  unit: MonitoringIntervalUnit;
}

/** The two schedules the UI offers; the backend rejects anything else. */
export type MonitoringSchedulePreset = 'DAILY' | 'WEEKLY';

export const MONITORING_SCHEDULE_PRESETS: Record<MonitoringSchedulePreset, MonitoringInterval> = {
  DAILY: { value: 1, unit: 'DAY' },
  WEEKLY: { value: 7, unit: 'DAY' },
};

const findPreset = (interval: MonitoringInterval | undefined): MonitoringSchedulePreset | undefined =>
  (Object.keys(MONITORING_SCHEDULE_PRESETS) as MonitoringSchedulePreset[]).find(
    (preset) =>
      MONITORING_SCHEDULE_PRESETS[preset].value === interval?.value &&
      MONITORING_SCHEDULE_PRESETS[preset].unit === interval?.unit
  );

export const presetOfInterval = (interval: MonitoringInterval | undefined): MonitoringSchedulePreset =>
  // An unknown pair (set through the API with a future preset) reads as daily rather than crashing the form.
  findPreset(interval) ?? 'DAILY';

const PRESET_LABEL_KEYS: Record<MonitoringSchedulePreset, string> = {
  DAILY: 'monitoringScheduleDaily',
  WEEKLY: 'monitoringScheduleWeekly',
};

/** Human label of an interval: the preset's name, or the raw pair (e.g. "1 MONTH") for one the UI does not offer. */
export const presetLabel = (interval: MonitoringInterval): string => {
  const preset = findPreset(interval);
  return preset ? i18n.t(PRESET_LABEL_KEYS[preset]) : `${interval.value} ${interval.unit}`;
};

export const sameInterval = (a: MonitoringInterval | undefined, b: MonitoringInterval | undefined): boolean =>
  a?.value === b?.value && a?.unit === b?.unit;
